import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import type {
  LocalQuestion,
  QuestionType,
  FormType,
  AssessmentType,
  FormBuilderService,
  FormBuilderStoreState,
  FeedbackForm,
} from '../types';
import { QUESTION_TYPES } from '../types';

interface FormBuilderStateReturn {
  // Form fields
  formName: string;
  setFormName: (v: string) => void;
  formDescription: string;
  setFormDescription: (v: string) => void;
  formType: FormType;
  setFormType: (v: FormType) => void;
  assessmentType: AssessmentType;
  setAssessmentType: (v: AssessmentType) => void;
  expiryDate: string;
  setExpiryDate: (v: string) => void;
  maxResponses: string;
  setMaxResponses: (v: string) => void;
  onePerBrowser: boolean;
  setOnePerBrowser: (v: boolean) => void;
  collectName: boolean;
  setCollectName: (v: boolean) => void;
  collectEmail: boolean;
  setCollectEmail: (v: boolean) => void;
  showCorrectAnswers: boolean;
  setShowCorrectAnswers: (v: boolean) => void;
  showReport: boolean;
  setShowReport: (v: boolean) => void;
  objectiveRatio: number;
  setObjectiveRatio: (v: number) => void;
  questions: LocalQuestion[];
  setQuestions: React.Dispatch<React.SetStateAction<LocalQuestion[]>>;

  // Actions
  resetForm: () => void;
  handleSaveDraft: () => Promise<void>;
  handlePublish: () => Promise<void>;
  getAvailableQuestionTypes: () => { value: QuestionType; label: string }[];

  // Store state
  editingFormId: string | null;
  saving: boolean;
  storeError: string | null;
}

export function useFormBuilderState(
  store: FormBuilderStoreState & FormBuilderService
): FormBuilderStateReturn {
  const { currentForm, editingFormId, saving, error: storeError, createForm, updateForm, clearCurrentForm } = store;

  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formType, setFormType] = useState<FormType>('feedback');
  const [assessmentType, setAssessmentType] = useState<AssessmentType>('objective');
  const [expiryDate, setExpiryDate] = useState('');
  const [maxResponses, setMaxResponses] = useState('');
  const [onePerBrowser, setOnePerBrowser] = useState(false);
  const [collectName, setCollectName] = useState(true);
  const [collectEmail, setCollectEmail] = useState(true);
  const [showCorrectAnswers, setShowCorrectAnswers] = useState(true);
  const [showReport, setShowReport] = useState(true);
  const [objectiveRatio, setObjectiveRatio] = useState(50);
  const [questions, setQuestions] = useState<LocalQuestion[]>([]);

  // Load form data when editing
  useEffect(() => {
    if (currentForm) {
      setFormName(currentForm.name);
      setFormDescription(currentForm.description || '');
      setFormType(currentForm.type);
      setAssessmentType(currentForm.assessmentType || 'objective');
      setExpiryDate(currentForm.expiryDate || '');
      setMaxResponses(currentForm.maxResponses?.toString() || '');
      setOnePerBrowser(currentForm.onePerBrowser);
      setCollectName(currentForm.collectName);
      setCollectEmail(currentForm.collectEmail);
      setShowCorrectAnswers(currentForm.showCorrectAnswers !== false);
      setShowReport(currentForm.showReport === true);
      setQuestions(currentForm.questions.map((q, idx) => ({
        id: q.id?.toString() || `temp-${idx}`,
        type: q.type,
        text: q.text,
        required: q.required,
        options: q.options,
        correctAnswers: q.correctAnswers,
        score: q.score,
        sliderMin: q.options ? (typeof q.options[0] === 'number' ? Number(q.options[0]) : 1) : 1,
        sliderMax: q.options ? (typeof q.options[1] === 'number' ? Number(q.options[1]) : 10) : 10,
      })));
    } else {
      resetForm();
    }
  }, [currentForm]);

  // Filter incompatible questions when assessment type changes
  useEffect(() => {
    if (formType !== 'assessment') return;

    let allowedTypes: string[];
    if (assessmentType === 'both') {
      allowedTypes = ['short_text', 'paragraph', 'slider', 'multiple_choice_single', 'multiple_choice_multi'];
    } else if (assessmentType === 'subjective') {
      allowedTypes = ['short_text', 'paragraph', 'slider'];
    } else {
      allowedTypes = ['multiple_choice_single', 'multiple_choice_multi'];
    }

    const incompatible = questions.filter(q => !allowedTypes.includes(q.type));
    if (incompatible.length > 0) {
      toast.warning(`${incompatible.length} incompatible question(s) removed.`);
      setQuestions(questions.filter(q => allowedTypes.includes(q.type)));
    }
  }, [formType, assessmentType]);

  const resetForm = useCallback(() => {
    setFormName('');
    setFormDescription('');
    setFormType('feedback');
    setAssessmentType('objective');
    setExpiryDate('');
    setMaxResponses('');
    setOnePerBrowser(false);
    setCollectName(true);
    setCollectEmail(true);
    setShowCorrectAnswers(true);
    setShowReport(true);
    setObjectiveRatio(50);
    setQuestions([]);
    clearCurrentForm();
  }, [clearCurrentForm]);

  const buildFormData = useCallback((status: 'draft' | 'active'): Omit<FeedbackForm, 'id' | 'uuid' | 'slug'> => ({
    name: formName.trim(),
    description: formDescription.trim() || undefined,
    type: formType,
    assessmentType: formType === 'assessment' ? assessmentType : undefined,
    status,
    expiryDate: expiryDate || null,
    maxResponses: maxResponses ? parseInt(maxResponses) : null,
    onePerBrowser,
    collectName,
    collectEmail,
    showCorrectAnswers,
    showReport,
    questions: questions.map(q => ({
      type: q.type,
      text: q.text,
      required: q.required,
      options: q.options,
      correctAnswers: q.correctAnswers,
      score: q.score,
    })),
  }), [formName, formDescription, formType, assessmentType, expiryDate, maxResponses, onePerBrowser, collectName, collectEmail, showCorrectAnswers, showReport, questions]);

  const validateForm = useCallback((): string | null => {
    if (!formName.trim()) return 'Please enter a survey name';
    if (!formDescription.trim()) return 'Please enter a survey description';
    if (!expiryDate) return 'Please select an expiry date';
    if (questions.length === 0) return 'Please add at least one question';

    // Warn about ratio mismatch for combined assessments
    if (formType === 'assessment' && assessmentType === 'both' && questions.length >= 2) {
      const objectiveTypes = ['multiple_choice_single', 'multiple_choice_multi'];
      const objCount = questions.filter(q => objectiveTypes.includes(q.type)).length;
      const currentObjPercent = Math.round((objCount / questions.length) * 100);
      const diff = Math.abs(currentObjPercent - objectiveRatio);
      if (diff > 30) {
        toast.warning(`Question ratio is off by ${diff}%. Target: ${objectiveRatio}% objective / ${100 - objectiveRatio}% subjective. Current: ${currentObjPercent}% objective.`);
      }
    }

    return null;
  }, [formName, formDescription, expiryDate, questions, formType, assessmentType, objectiveRatio]);

  const handleSaveDraft = useCallback(async () => {
    const err = validateForm();
    if (err) { toast.error(err); return; }

    const formData = buildFormData('draft');
    if (editingFormId) {
      const result = await updateForm(editingFormId, formData);
      if (result.success) toast.success('Draft saved successfully!');
      else toast.error(result.error || 'Failed to save draft');
    } else {
      const result = await createForm(formData);
      if (result.success) { toast.success('Draft created successfully!'); resetForm(); }
      else toast.error(result.error || 'Failed to create draft');
    }
  }, [validateForm, buildFormData, editingFormId, updateForm, createForm, resetForm]);

  const handlePublish = useCallback(async () => {
    const err = validateForm();
    if (err) { toast.error(err); return; }

    const formData = buildFormData('active');
    if (editingFormId) {
      const result = await updateForm(editingFormId, formData);
      if (result.success) toast.success('Survey published successfully!');
      else toast.error(result.error || 'Failed to publish');
    } else {
      const result = await createForm(formData);
      if (result.success) {
        const publicUrl = `${window.location.origin}/feedback/${result.data?.slug}`;
        navigator.clipboard.writeText(publicUrl);
        toast.success('Survey published! Public link copied.');
        resetForm();
      } else {
        toast.error(result.error || 'Failed to publish');
      }
    }
  }, [validateForm, buildFormData, editingFormId, updateForm, createForm, resetForm]);

  const getAvailableQuestionTypes = useCallback(() => {
    if (formType === 'assessment') {
      if (assessmentType === 'both') {
        return QUESTION_TYPES.filter(t => ['short_text', 'paragraph', 'slider', 'multiple_choice_single', 'multiple_choice_multi'].includes(t.value));
      }
      if (assessmentType === 'subjective') {
        return QUESTION_TYPES.filter(t => ['short_text', 'paragraph', 'slider'].includes(t.value));
      }
      return QUESTION_TYPES.filter(t => ['multiple_choice_single', 'multiple_choice_multi'].includes(t.value));
    }
    return QUESTION_TYPES;
  }, [formType, assessmentType]);

  return {
    formName, setFormName, formDescription, setFormDescription,
    formType, setFormType, assessmentType, setAssessmentType,
    expiryDate, setExpiryDate, maxResponses, setMaxResponses,
    onePerBrowser, setOnePerBrowser, collectName, setCollectName,
    collectEmail, setCollectEmail, showCorrectAnswers, setShowCorrectAnswers,
    showReport, setShowReport, objectiveRatio, setObjectiveRatio, questions, setQuestions,
    resetForm, handleSaveDraft, handlePublish, getAvailableQuestionTypes,
    editingFormId, saving, storeError,
  };
}
